import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Dialog } from '@mui/material';

import OnDeleteIcon from '../../../icons/OnDeleteIcon';
import { uuid } from '../../../utils/stringUtils';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';

import { validateCard } from './validateEducation';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';
import local from './education.module.css';

const MAX_INSTITUTION_LENGTH = 200;
const MAX_SUBJECT_LENGTH = 100;
const MAX_GRADE_LENGTH = 20;

const EditEducationCard = ({
    institution,
    currentParticipant,
    qualificationTypes,
    isDisabled,
    editType,
    isOpen,
    onCancel,
    onUpdate,
    onAdd
}) => {
    // LOCAL STATE
    const initialState = {
        id: uuid(),
        name: '',
        qualificationTypeId: '',
        subjects: [],
        startDate: '',
        endDate: '',
        onGoing: false
    };
    const initialSubjectState = { subject: '', grade: '' };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [type, setType] = useState('edit');
    const [currentSubject, setCurrentSubject] = useState(initialSubjectState);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [displaySubjectInputs, setDisplaySubjectInputs] = useState(true);

    // USEEFFECTS
    useEffect(() => {
        setType(editType);
        if (editType === 'edit') {
            setNewEntry(institution);
            setInitialStateOnEntry(institution);
        } else setNewEntry((prev) => ({ ...prev, participantId: currentParticipant?.id }));
    }, [editType, currentParticipant]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdateValue = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateCurrentSubject = (key, value) => {
        clearError('subjects');
        setCurrentSubject((prev) => ({ ...prev, [key]: value }));
    };

    const onDeleteSubject = (idx) => {
        if (newEntry.subjects.length === 1 && !displaySubjectInputs) setDisplaySubjectInputs(true);
        const subjects = newEntry.subjects.filter((el, i) => i !== idx);
        setNewEntry((prev) => ({ ...prev, subjects }));
    };

    const onDeleteCurrentSubject = () => {
        setCurrentSubject(initialSubjectState);
        if (newEntry.subjects.length) setDisplaySubjectInputs(false);
    };

    const onClose = () => {
        setNewEntry(initialState);
        setErrors({});
        onCancel();
    };

    const onOnGoingChange = (option) => {
        clearError('endDate');
        option
            ? setNewEntry((prev) => ({ ...prev, onGoing: true, endDate: '' }))
            : setNewEntry((prev) => ({ ...prev, onGoing: false }));
    };

    const onAddNewSubjectFields = () => {
        if (displaySubjectInputs)
            setNewEntry((prev) => ({ ...prev, subjects: [...newEntry.subjects, currentSubject] }));
        setCurrentSubject(initialSubjectState);
        !displaySubjectInputs && setDisplaySubjectInputs(true);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const updatedEntry = currentSubject?.subject
            ? { ...newEntry, subjects: [...newEntry.subjects, currentSubject] }
            : newEntry;
        const newErrors = validateCard(
            updatedEntry,
            initialStateOnEntry,
            type,
            currentParticipant?.dateOfBirth
        );
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        type === 'create' ? onAdd(updatedEntry) : onUpdate(updatedEntry);
        setNewEntry(initialState);
        setErrors({});
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader} id="past-education-modal-title">
                <h3>{type === 'create' ? 'Add' : 'Edit'} Past Education</h3>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="past-education-edit-form">
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <TextInputField
                                id="name"
                                label="Institution Name"
                                placeholder="Enter name..."
                                mandatory={true}
                                disabled={isDisabled}
                                maxLength={MAX_INSTITUTION_LENGTH}
                                value={newEntry.name || ''}
                                error={errors.name}
                                onChange={(e) => onUpdateValue('name', e.target.value)}
                            />

                            <SingleSelect
                                id="qualificationTypeId"
                                label="Qualification Type"
                                placeholder="Select qualification type..."
                                disabled={isDisabled}
                                mandatory={true}
                                customClass="absolutePosition"
                                menuItems={qualificationTypes}
                                selectedId={newEntry.qualificationTypeId}
                                selected={
                                    qualificationTypes?.find(
                                        (el) => el.id === newEntry.qualificationTypeId
                                    ) || {}
                                }
                                error={errors.qualificationTypeId}
                                onChange={(chosenId) =>
                                    onUpdateValue('qualificationTypeId', chosenId)
                                }
                            />
                        </div>
                    </div>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <div>
                                {newEntry.subjects.map((el, i) => (
                                    <div key={i} className={local.gridFieldsStatic}>
                                        <span>{el.subject}</span>
                                        <span>{el.grade}</span>
                                        <OnDeleteIcon
                                            active={!isDisabled}
                                            onDelete={() => onDeleteSubject(i)}
                                        />
                                    </div>
                                ))}
                            </div>
                            {(displaySubjectInputs || !newEntry.subjects.length) && (
                                <div className={local.gridFields}>
                                    <TextInputField
                                        id="subjects"
                                        label="Subject"
                                        placeholder="Enter subject..."
                                        mandatory={newEntry.subjects.length < 1}
                                        disabled={isDisabled}
                                        maxLength={MAX_SUBJECT_LENGTH}
                                        value={currentSubject.subject || ''}
                                        error={errors.subjects}
                                        onChange={(e) =>
                                            onUpdateCurrentSubject('subject', e.target.value)
                                        }
                                    />
                                    <TextInputField
                                        id="grade"
                                        label="Grade"
                                        placeholder="Enter grade..."
                                        disabled={isDisabled}
                                        maxLength={MAX_GRADE_LENGTH}
                                        value={currentSubject.grade || ''}
                                        error={errors.grade}
                                        onChange={(e) =>
                                            onUpdateCurrentSubject('grade', e.target.value)
                                        }
                                    />
                                    {newEntry.subjects.length > 0 && (
                                        <div className={local.gridFieldIcon}>
                                            <OnDeleteIcon
                                                active={!isDisabled}
                                                onDelete={onDeleteCurrentSubject}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <Button
                                id="addNewSubjectFields"
                                type="button"
                                disabled={
                                    isDisabled || (!currentSubject.subject && displaySubjectInputs)
                                }
                                customClass="noButtonTopMargin"
                                content="Add Subject"
                                onClick={() => onAddNewSubjectFields()}
                            />
                        </div>
                    </div>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <div className={form.formColumnSplit}>
                                <DateSelect
                                    id="startDate"
                                    label="Start Date"
                                    disabled={isDisabled}
                                    value={newEntry.startDate}
                                    error={errors.startDate}
                                    onDateChange={(date) => onUpdateValue('startDate', date)}
                                />
                                <DateSelect
                                    id="endDate"
                                    label="End Date"
                                    disabled={isDisabled || newEntry.onGoing}
                                    value={newEntry.endDate}
                                    error={errors.endDate}
                                    onDateChange={(date) => onUpdateValue('endDate', date)}
                                />
                            </div>
                            <RadioButtons
                                id="onGoing"
                                label="Education Ongoing?"
                                disabled={isDisabled}
                                value={newEntry.onGoing}
                                onChange={(option) => onOnGoingChange(option)}
                            />
                        </div>
                    </div>

                    <div className={modal.actionButtons}>
                        <Button
                            id="updatePastEducationButton"
                            disabled={isDisabled || isUpdateButtonDisabled}
                            content={type === 'edit' ? 'Update' : 'Add'}
                            customErrorClass="formActionsError"
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                        <Button
                            id="editEducationCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onClose}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

EditEducationCard.propTypes = {
    institution: PropTypes.object,
    currentParticipant: PropTypes.object,
    qualificationTypes: PropTypes.array,
    isDisabled: PropTypes.bool,
    editType: PropTypes.string,
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func,
    onUpdate: PropTypes.func,
    onAdd: PropTypes.func
};
export default EditEducationCard;
